import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Call real backend API
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 200) {
        toast({
          title: "Message Sent!",
          description: "Thank you for contacting us. We'll get back to you as soon as possible.",
        });
        // Clear form
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or email us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-6">
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Contact Us
            </span>
          </h2>
          
          <p className="text-center text-gray-400 mb-12 text-lg">
            If you have any questions, please reach out to us via email or by our web contact form. We'll answer promptly!
          </p>

          {/* Contact Info */}
          <div className="text-center mb-8 space-y-2">
            <p className="text-white font-semibold text-xl">Cheer Wave Productions</p>
            <a href="mailto:info@cheerwaveproductions.com" className="text-orange-400 hover:text-orange-300 transition-colors block">
              info@cheerwaveproductions.com
            </a>
            <a href="https://www.instagram.com/cheerwaveproductions?igsh=MTNucWs1aWRheTFhZQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 transition-colors block">
              @cheerwaveproductions
            </a>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="bg-gradient-to-br from-gray-900 to-black border border-orange-500/30 rounded-xl p-8 shadow-2xl space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-gray-800 border-gray-700 text-white focus:border-orange-500 focus:ring-orange-500"
                  placeholder="Your name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-gray-800 border-gray-700 text-white focus:border-orange-500 focus:ring-orange-500"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject" className="text-white">Subject</Label>
              <Input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="bg-gray-800 border-gray-700 text-white focus:border-orange-500 focus:ring-orange-500"
                placeholder="What's this about?"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-white">Message</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="bg-gray-800 border-gray-700 text-white focus:border-orange-500 focus:ring-orange-500 resize-none"
                placeholder="Tell us what you need..."
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-3 rounded-lg transition-all hover:shadow-lg hover:shadow-orange-500/50"
            >
              <Send className="mr-2 h-5 w-5" />
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;